import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { LoHiUserInfo } from '../../../root/auth.service';
import { NetworkActiveComponent } from '../../../shared/networkActive.component';
import { SnackBarService } from '../../../shared/snackbar.service';
import { isClientError, isHttpErrorResponse } from '../../../utilities/isHttpErrorResponse';
import { luhn36Check } from '../../../utilities/luhn36';
import {
  OmmaUploadService,
  UploadResponse,
  UploadResponseDriverNoCompanyError,
  UploadResponseDriverNotFoundError,
} from '../omma-upload.service';

@Component({
  selector: 'lohi-upload-propx',
  templateUrl: './upload-propx.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadPropxComponent extends NetworkActiveComponent implements OnInit {
  @ViewChild('addDriverMatchingModal') private addDriverMatchingModal: TemplateRef<any>;

  public document$$ = new BehaviorSubject<File | null>(null);
  public responses$$ = new BehaviorSubject<UploadResponse[]>([]);

  public errorResponses$: Observable<UploadResponse[]>;
  public uploadedResponses$: Observable<UploadResponse[]>;
  public alreadyUploadedResponses$: Observable<UploadResponse[]>;

  public driverNamesAdded$$ = new BehaviorSubject<string[]>([]);

  public driverNameForMatching: string | null;
  public driverIdForMatchingControl = new FormControl();
  public driverIdForMatchingValid = false;

  public modalRef: MatDialogRef<any>;

  public uploadType: string;

  public userMappingsCreated: { [key: string]: LoHiUserInfo } = {};

  constructor(
    private ommaUploadService: OmmaUploadService,
    private snackbarService: SnackBarService,
    private logger: NGXLogger,
    private matDialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private cd: ChangeDetectorRef,
  ) {
    super();

    this.errorResponses$ = this.responses$$.pipe(map((responses) => responses.filter((r) => r.hasError)));
    this.uploadedResponses$ = this.responses$$.pipe(map((responses) => responses.filter((r) => r.uploaded)));
    this.alreadyUploadedResponses$ = this.responses$$.pipe(
      map((responses) => responses.filter((r) => r.alreadyUploaded)),
    );

    this.driverIdForMatchingControl.valueChanges.subscribe((driverId: string) => {
      if (driverId.length === 11 && luhn36Check(driverId)) {
        this.driverIdForMatchingValid = true;
      } else {
        this.driverIdForMatchingValid = false;
      }
    });
  }

  public ngOnInit(): void {
    this.activatedRoute.data.pipe(take(1)).subscribe((data) => (this.uploadType = data.uploadType));
  }

  public onFileChange(event: any) {
    if (event.target.files && event.target.files[0]) {
      const [file] = event.target.files;
      this.document$$.next(file);
    }
  }

  public upload() {
    if (!this.document$$.value) {
      return;
    }
    this.responses$$.next([]);
    this.driverNamesAdded$$.next([]);
    this.doUpload$(this.document$$.value).subscribe({
      next: (response) => {
        this.responses$$.next(response);
      },
      error: (error) => {
        this.logger.error(error);
        if (isHttpErrorResponse(error) && isClientError(error.error)) {
          this.snackbarService.showError(error.error.messageToUser);
        } else {
          this.snackbarService.showError('something went wrong, please contact a dev');
        }
      },
    });
  }

  private doUpload$(file: File): Observable<UploadResponse[]> {
    switch (this.uploadType) {
      case 'propx':
        return this.ommaUploadService.uploadPropx$(file);
      case 'sandi':
        return this.ommaUploadService.uploadSandi$(file);
      case 'eog':
        return this.ommaUploadService.uploadEOG$(file);
      case 'solaris':
        return this.ommaUploadService.uploadSolaris$(file);
      case 'cig':
        return this.ommaUploadService.uploadCIG$(file);
      default:
        return null;
    }
  }

  // not great angular coding here, but internal so idc
  public isString(v: any): string {
    if (typeof v === 'string') {
      return v;
    }
    return null;
  }

  public isDriverNotFoundErr(v: any): UploadResponseDriverNotFoundError {
    if (typeof v === 'object' && v.errorType === 'driver_not_found') {
      return v;
    }
    return null;
  }

  public isNoCompanyAtAcceptError(v: any): UploadResponseDriverNoCompanyError {
    if (typeof v === 'object' && v.errorType === 'driver_no_company') {
      return v;
    }
    return null;
  }

  public promptAddDriver(driverName: string) {
    this.driverNameForMatching = driverName;
    this.driverIdForMatchingControl.setValue('');
    this.modalRef = this.matDialog.open(this.addDriverMatchingModal, {
      panelClass: ['w-full', 'lg:w-1/3'],
    });
  }

  public async addDriver() {
    if (this.networkActive$$.value) {
      return;
    }

    try {
      this.networkActive$$.next(true);

      const driverAdded = await this.ommaUploadService
        .addUserNameMatching$(this.driverNameForMatching, this.driverIdForMatchingControl.value)
        .toPromise();

      this.userMappingsCreated[this.driverNameForMatching] = driverAdded?.driver;

      this.snackbarService.showMessage(
        `Added driver ${this.driverNameForMatching}. Matched to LoHi user ${driverAdded?.driver?.driverName} (${driverAdded?.driver?.phoneNumber})`,
      );
      this.modalRef.close();
      const nextNameList = this.driverNamesAdded$$.value;
      nextNameList.push(this.driverNameForMatching);
      this.driverNamesAdded$$.next(nextNameList);
      this.cd.markForCheck();
    } catch {
      this.snackbarService.showError('Failed to add driver');
    } finally {
      this.networkActive$$.next(false);
    }
  }

  public nameAdded(name: string): boolean {
    return this.driverNamesAdded$$.value.some((n) => n === name);
  }
}
